/**
 * Jobs Component
 *
 * Angular 10 Upgrade - Modified by Mihir
 * Date: May 7, 2026
 *
 * Changes Made:
 * 1. Added ChangeDetectorRef import (line 1)
 * 2. Injected ChangeDetectorRef in constructor
 * 3. Added manual change detection in data loading callbacks
 *
 * NEW IMPORT ADDED:
 * import { ChangeDetectorRef } from '@angular/core';
 *
 * Reason for Change:
 * Angular 10 has stricter change detection. When data is loaded asynchronously
 * (like from API calls), the view may not automatically update. This was causing
 * dropdowns (Main Assistant, Project Manager, Foreman) to appear empty even though
 * data was successfully fetched from the backend.
 *
 * Solution: Manually trigger change detection after async data loads using cdr.detectChanges()
 */

import { Component, OnInit, ViewChild, Renderer2, Inject, ChangeDetectorRef } from '@angular/core';
import { DepartmentService } from 'src/app/service/department/department.service';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { JobsService } from 'src/app/service/jobs/jobs.service';
import { UsersService } from 'src/app/service/users/users.service';
import { LocalstorageService } from 'src/app/service/shared/localstorage/localstorage.service';
import { DOCUMENT } from '@angular/common';
import { OwnerInfoService } from 'src/app/service/owner-info/owner-info.service';
import { JobAuditTrailService } from 'src/app/service/job-audit-trail/job-audit-trail.service';
import { IpAddressService } from 'src/app/service/ip-address/ip-address.service';
import { MomentService } from 'src/app/service/shared/moment/moment.service';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import getAndParseUserPermissions from 'src/app/helpers/parsePermissions';

@Component({
  selector: 'app-jobs',
  templateUrl: './jobs.component.html',
  styleUrls: ['./jobs.component.scss'],
})
export class JobsComponent implements OnInit {
  @ViewChild('closebutton') closebutton;
  @ViewChild('closebutton1') closebutton1;
  @ViewChild('closebutton2') closebutton2;
  @ViewChild('closeFilterModel') closeFilterModel;
  @ViewChild('new_job_id') new_job_id;
  @ViewChild('createNewJob') createNewJob;
  @ViewChild('name') name;

  curPage: number = 1;
  pageSize: number = 50;
  pages;

  departmentsData: any = [];
  jobsData = [];
  projectManagerData: any = [];
  ProjectAssistantData: any = [];
  projectForemanData: any = [];
  ownersData: any = [];
  jobDetail;
  sortMode = 'asc';

  subDepartmentsData = [];
  searchInfo = {
    project_manager_id: '',
    assistant_id: '',
    foreman_id: '',
    status: '',
    search: '',
    department_codes: '',
    owner_id: '',
    start: 0,
    field: '',
    order: '',
    year: '16',
    is_precon_involved: '',
    turnover_complete: '',
    submit_to_accounting: '',
  };
  userPermissions;
  job_number;
  new_job_number;
  new_job_name;
  deleteJobId;
  userId;
  ipAddress;
  bkData;
  newAddedDepartments;
  addJobForm: FormGroup;
  loadJobNo = false;
  newJobId;
  auditTrailData;
  dtPage = 1;
  dtStart = 0;
  dtEnd = 50;
  jobsCouter = 0;
  jobBkData;
  jobExtra = [1, 'desc'];
  init = true;
  lastSortStatus;
  yearFilterData: any = [];
  activeJobCount: number;

  /**
   * Constructor
   *
   * Angular 10 Upgrade - Modified by Mihir
   * NEW DEPENDENCY ADDED: ChangeDetectorRef
   *
   * This is required to manually trigger change detection when async data loads.
   * Without this, dropdowns would not populate even though API calls succeed.
   */
  constructor(
    private departmentsService: DepartmentService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private jobsService: JobsService,
    private localStorageService: LocalstorageService,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private _document,
    private usersService: UsersService,
    private ownerInfoService: OwnerInfoService,
    private jobAuditTrailService: JobAuditTrailService,
    private ipAddressService: IpAddressService,
    private momentService: MomentService,
    private loaderService: LoaderService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef, // ADDED for Angular 10 - Mihir
  ) {
    this.loaderService.displayLoader();

    this.userId = this.localStorageService.getItem('userId');

    //To get saved filter data if exist
    const filterData = this.localStorageService.getItem('jobFilter');
    if (filterData) {
      this.searchInfo = JSON.parse(filterData);
    }

    //Function call to get all department data
    this.getDepartments();
    //Function call to get all users data
    this.getUsers();
    //Function call to get jobs data
    this.getJobs();
    //Function call to get owners data
    this.getOwners();
    //Function call to get year data
    this.getYearFilters();
    //Function call to get client ip address
    this.getIP();
    //Function call to get last entry of job
    this.getLastEntryOfJob();

    this.getTableFilters();
    setTimeout(() => {
      //Function call to generate from
      this.createForm();
      this.loadSelect2();
      this.doneSearchTyping();
      this.setSelect2MultipleWithCheck();
    }, 300);
  }

  ngOnInit(): void {
    this.userPermissions = getAndParseUserPermissions(this.localStorageService);
    setTimeout(() => this.loadSelect2MultipleWithCheck(), 700);
  }

  loadSelect2() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadSelect2();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  loadFocus() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadFocus();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  loadSelect2Error() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadSelect2Error();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  triggerChange(obj) {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `triggerChange('` + obj + `');`;
    this.renderer2.appendChild(this._document.body, s);
  }

  triggerClick(obj) {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `triggerClick('` + obj + `');`;
    this.renderer2.appendChild(this._document.body, s);
  }

  loadInputFormControlError(obj, action) {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadInputFormControlError('` + obj + `','` + action + `')`;
    this.renderer2.appendChild(this._document.body, s);
  }

  setAttribute() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `setAttribute()`;
    this.renderer2.appendChild(this._document.body, s);
  }

  loadTooltip() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadTooltip();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  loadScroller() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadScroller();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  doneSearchTyping() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `doneSearchTyping();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  //Create form with/without data
  createForm() {
    //Create blank form group for new job
    this.addJobForm = this.formBuilder.group({
      name: ['', Validators.required],
      project_manager_id: ['', Validators.required],
      job_creation_date: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, ''),
      assistant_id: ['', Validators.required],
      sub_department_number: ['', Validators.required],
      main_job: ['', Validators.required],
      contract_size: ['1'],
      submitted_by: this.userId,
      created_date: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, ''),
      updated_date: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, ''),
      is_deleted: 0,
      status: 1,
    });
  }

  //To clear datatable
  clearDatatable() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `clearDatatable('.jobs-data-table')`;
    this.renderer2.appendChild(this._document.body, s);
  }

  //To draw datatable with data
  drawDatatable() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `reloadDataTable('.jobs-data-table',[` + this.jobExtra[0] + `,'` + this.jobExtra[1] + `'])`;
    this.renderer2.appendChild(this._document.body, s);
  }

  //To get all departments
  getDepartments() {
    //Service call to get all departments data
    this.departmentsService.getDepartments().subscribe(
      res => this.onDepartmentSuccess(res),
      error => this.onDepartmentsError(error),
    );
  }

  //On success of departments api call
  onDepartmentSuccess(result) {
    if (!result?.data) return;

    //Assign department result data to departmentsData
    this.departmentsData = result.data.filter(item => item.status == 1);
  }

  //On error of departments api call
  onDepartmentsError(error) {}

  //To get all jobs
  getJobs(func: string = 'getJobs') {
    //Service call to get all jobs data
    this.jobsService[func](this.searchInfo, true, true).subscribe(
      res => this.onJobsSuccess(res),
      error => this.onJobsError(error),
    );
  }

  //On success of jobs api call
  onJobsSuccess(result) {
    if (!result?.data?.data) {
      this.loaderService.hideLoader();
      return;
    }

    // TODO: set activeJobCount in this function.
    if (this.curPage > this.numberOfPages()) {
      this.curPage = 1;
    }

    this.clearDatatable();

    this.jobsCouter = result.data.count;
    this.jobBkData = result.data.data;
    this.jobsData = result.data.data;

    this.activeJobCount = result.data?.activeJobCount || 0;

    this.drawDatatable();
    this.getPages();
    this.setAttribute();
    this.loaderService.hideLoader();

    setTimeout(() => {
      this.loadTooltip();

      if (this.loadJobNo) {
        const jobNumber = this.newJobId;
        this.getJobDetailsById(jobNumber);
      }
      this.loadScroller();
    }, 1000);
  }

  getJobDetailsById(jobId: number) {
    this.jobsService.getJobById(jobId).subscribe(
      res => {
        if (!res?.data) return;

        this.new_job_number = res.data[0].number;
        this.new_job_name = res.data[0].name;
        this.loadJobNo = false;
      },
      error => console.error(error),
    );
  }

  sortByKey(array, key, type = 'asc') {
    return [...array].sort((a, b) => {
      const x = a[key];
      const y = b[key];
      if (type == 'asc') {
        return x < y ? -1 : x > y ? 1 : 0;
      } else {
        return x > y ? -1 : x < y ? 1 : 0;
      }
    });
  }

  numberOfPages() {
    if (!this.jobBkData) return;

    return Math.ceil(this.jobsCouter / this.pageSize);
  }

  checkLength(param: 'next' | string) {
    if (!this.jobBkData) return false;

    if (param === 'next') {
      return this.curPage >= this.jobsCouter / this.pageSize;
    }

    return this.curPage == 1;
  }

  getPageNum(): [number, number] {
    if (this.numberOfPages() <= 10) {
      // less than 10 total pages so show all
      return [1, this.numberOfPages()];
    }

    // more than 10 total pages so calculate start and end pages
    if (this.curPage <= 6) {
      return [1, 10];
    } else if (this.curPage + 4 >= this.numberOfPages()) {
      const numPages = this.numberOfPages();

      return [numPages - 9, numPages];
    }

    return [this.curPage - 5, this.curPage + 4];
  }

  getPages() {
    const [startPage, endPage] = this.getPageNum();

    // create an array of pages to ng-repeat in the pager control
    const pages = [...Array(endPage + 1 - startPage).keys()].map(i => startPage + i);

    const val = pages.length > 1 ? pages : [];
    this.pages = val;
  }

  changeCurrentPage(param) {
    let start, end;
    if (param == 'next') {
      if (this.curPage < this.numberOfPages()) {
        this.curPage += 1;
      }
    } else if (param == 'prev') {
      if (this.curPage > 1) {
        this.curPage -= 1;
      }
    } else if (param == 'first') {
      if (this.curPage != 1) {
        this.curPage = 1;
      }
    } else if (param == 'last') {
      if (this.curPage != this.numberOfPages()) {
        this.curPage = this.numberOfPages();
      }
    } else {
      this.curPage = param;
    }

    if (this.curPage > 0 && this.curPage <= this.numberOfPages()) {
      this.searchInfo['start'] = this.curPage * 50 - 50;
      start = this.curPage * 50 - 50;
      end = start + 50;

      this.getJobs();
    }
  }

  //On error of jobs api call
  onJobsError(error) {
    this.loaderService.hideLoader();
  }

  sortTable(field, event) {
    if (field != '' && event) {
      this.searchInfo['field'] = field;
      if (event.target.ariaSort == null) {
        this.searchInfo['order'] = 'asc';
      } else if (event.target.ariaSort == 'ascending') {
        this.searchInfo['order'] = 'desc';
      } else if (event.target.ariaSort == 'descending') {
        this.searchInfo['order'] = 'asc';
      }

      if (field == 'j.name') {
        this.jobExtra = [0, this.searchInfo['order']];
      } else if (field == 'j.number') {
        this.jobExtra = [1, this.searchInfo['order']];
      } else if (field == 'departments_list') {
        this.jobExtra = [2, this.searchInfo['order']];
      } else if (field == 'o.owner_name') {
        this.jobExtra = [3, this.searchInfo['order']];
      } else if (field == 'f.facility_name') {
        this.jobExtra = [4, this.searchInfo['order']];
      } else if (field == 'j.created_date') {
        this.jobExtra = [5, this.searchInfo['order']];
      } else if (field == 'project_manager_name') {
        this.jobExtra = [6, this.searchInfo['order']];
      } else if (field == 'project_assistant_name') {
        this.jobExtra = [7, this.searchInfo['order']];
      } else if (field == 'project_foreman_name') {
        this.jobExtra = [8, this.searchInfo['order']];
      } else if (field == 'j.submit_to_accounting') {
        this.jobExtra = [11, this.searchInfo['order']];
      } else if (field == 'j.contact_tm') {
        this.jobExtra = [9, this.searchInfo['order']];
      } else if (field == 'j.status') {
        this.jobExtra = [10, this.searchInfo['order']];
      }

      this.getJobs();
    }
  }

  getTableFilters() {
    const filters = ['assistants', 'foremans', 'managers'];
    this.jobsService.getJobTableFilters(filters, false, false, { allAssistants: true }).subscribe(
      res => this.onFiltersSuccess(res),
      err => this.onFiltersError(err),
    );
  }

  /**
   * On Filters Success
   *
   * Angular 10 Upgrade - Modified by Mihir
   * ADDED: this.cdr.detectChanges() at the end
   *
   * BEFORE (Angular 9):
   * Data was assigned but view didn't update automatically
   *
   * AFTER (Angular 10):
   * Manually trigger change detection to update the view
   *
   * This fixes the issue where:
   * - ProjectAssistantData dropdown was empty
   * - projectForemanData dropdown was empty
   * Even though API returned data successfully
   */
  onFiltersSuccess(result) {
    if (!result?.data) return;

    const { assistants = [], foremans = [] } = result?.data?.data ?? {};

    this.ProjectAssistantData = assistants;
    this.projectForemanData = foremans;

    // Angular 10 Fix - Trigger change detection to update the view - Mihir
    this.cdr.detectChanges();
  }

  getUsers() {
    //Service call to get all users data
    this.usersService.getUsers().subscribe(
      res => this.onUsersSuccess(res),
      error => console.error(error),
    );
  }

  //On success of users api call
  onUsersSuccess(result) {
    if (!result?.data) {
      return;
    }

    //Assign users result data to projectManagerData
    this.projectManagerData = result.data.filter(function (item) {
      return (item.role_id == 4 || item.role_id == 14) && item.status == 1;
    });

    // Angular 10 Fix - Trigger change detection to update the view - Mihir
    this.cdr.detectChanges();
  }

  onFiltersError(error: unknown) {
    console.error('GET Table Filters Error:', error);
  }

  //On new job submit
  onNewJobSubmit(form, continueForm = '') {
    // this.newAddedDepartments = [];
    if (form.value.name == '' || form.value.name == null || form.value.name == undefined) {
      this.loadInputFormControlError('#name', 'add');
    } else {
      this.loadInputFormControlError('#name', 'remove');
    }
    if (form.invalid) {
      this.addJobForm.markAllAsTouched();
      setTimeout(() => {
        this.loadSelect2Error();
      }, 200);
    } else {
      // form.value.number = this.job_number;
      form.value.submitted_by = this.userId;
      form.value.job_creation_date = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');
      form.value.created_date = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');
      form.value.updated_date = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');
      form.value.is_deleted = 0;
      form.value.status = 1;
      this.localStorageService.setItem('newJobData', JSON.stringify(form.value));
      this.closebutton.nativeElement.click();
      setTimeout(() => {
        this.createJob(form, continueForm);
      }, 200);
    }
  }

  createJob(form, continueForm = '') {
    this.jobsService.createJob(form.value).subscribe(
      res => this.onJobAddSuccess(res, continueForm),
      error => this.onJobAddError(error),
    );
  }

  //On success of job add api call
  onJobAddSuccess(result, continueForm = '') {
    if (!result?.data) {
      this.clearForm();
      return;
    }

    let newAuditTrail = {
      job_id: result.data.id,
      user_id: this.userId,
      ip_address: this.ipAddress,
      action: 0,
      status_from: 1,
      status_to: 2,
      working_status_from: 0,
      working_status_to: 0,
      created_date: this.momentService.getDBDateTimeFormat(),
    };

    this.createJobAuditTrail(newAuditTrail);

    let newAuditTrail1 = {
      job_id: result.data.id,
      user_id: this.userId,
      ip_address: this.ipAddress,
      action: 0,
      status_from: 0,
      status_to: 0,
      working_status_from: 0,
      working_status_to: 1,
      created_date: this.momentService.getDBDateTimeFormat(),
    };

    this.createJobAuditTrail(newAuditTrail1);

    if (continueForm != '') {
      this.router.navigate([result.data.id], {
        relativeTo: this.activatedRoute,
      });
    } else {
      this.newJobId = result.data.id;
      //Function call to get last entry of job
      this.getLastEntryOfJob();
      this.getJobs();
      this.loadJobNo = true;
      this.new_job_name = '';
      this.new_job_number = '';
      this.new_job_id.nativeElement.click();
    }

    this.clearForm();
  }

  //On error of job add api call
  onJobAddError(error) {}

  getLastEntryOfJob() {
    //Get latest entry of jobs to set new job number
    this.jobsService.getJobLastEntry().subscribe(
      res => this.onJobGetSuccess(res),
      error => this.onJobGetError(error),
    );
  }

  //On success of latest job get api call
  onJobGetSuccess(result) {
    if (!result) {
      return;
    }

    let currentYear = new Date().getFullYear().toString().substr(2, 2);
    if (result.data.length > 0) {
      let lastYear = result.data[0].number.substr(0, 2);
      let increamentedNumber = result.data[0].number.substr(3, 4);
      if (lastYear < currentYear) {
        increamentedNumber = '0000';
      }

      this.job_number = currentYear + '-' + ('000' + (Number(increamentedNumber) + 1)).slice(-4);
    } else {
      this.job_number = currentYear + '-' + '0001';
    }
  }

  //On error of latest job get api call
  onJobGetError(error) {}

  //To store selected sub departments based on selection
  storeSubDepartments(event, id) {
    if (event.target.checked) {
      if (this.subDepartmentsData.length == 0) {
        this.setDataToControl('main_job', id);
        this.triggerChange('#mainDepartmentSelect');
      }
      //If select add value to an array
      this.subDepartmentsData.push(id);
      //Set value to form group parameter
      this.addJobForm.get('sub_department_number').setValue(this.subDepartmentsData.sort());
    } else {
      //If deselect remove value from an array
      this.subDepartmentsData.forEach((item, index) => {
        if (item == id) this.subDepartmentsData.splice(index, 1);
      });
      if (id == this.addJobForm.get('main_job').value) {
        if (this.subDepartmentsData.length > 0) {
          this.setDataToControl('main_job', this.subDepartmentsData[0]);
        } else {
          this.setDataToControl('main_job', '');
        }
      }
      this.triggerChange('#mainDepartmentSelect');
      //Set value to form group parameter
      this.addJobForm.get('sub_department_number').setValue(this.subDepartmentsData.sort());
    }
    let newArr = [],
      i = 1;
    this.subDepartmentsData.forEach(result => {
      let shortCode = '';
      let depFilterResult = this.departmentsData.filter(function (item) {
        return item.id == result;
      });
      if (depFilterResult.length > 0) {
        shortCode = depFilterResult[0].department_short_code;
      }
      if (result == this.addJobForm.get('main_job').value) {
        newArr[0] = shortCode;
      } else {
        newArr[i] = shortCode;
        i++;
      }
      if (this.subDepartmentsData.length == i) {
        this.newAddedDepartments = newArr.toString();
      }
    });
    this.loadErrorStatus();
  }

  //To set main job to sub job departments
  setSubDepartments(_, value) {
    const data = this.subDepartmentsData.filter(result => value == result);

    if (!data.length) {
      this.triggerClick('#inlineCheckbox' + value);
      this.subDepartmentsData.push(value);
      this.addJobForm.get('sub_department_number').setValue(this.subDepartmentsData.sort());
    }
    this.setDataToControl('main_job', value);
  }

  callDoneTyping(_) {
    this.doneSearchTyping();
  }

  //Set search parameter and get filter data
  searchInTable(value: string, key: 'search' | string, value1 = '', key1 = '') {
    if (!value) {
      delete this.searchInfo[key];
    }

    if (key1 != '') {
      if (value1 != '') {
        this.searchInfo[key1] = value1;
      } else if (value1 == '') {
        delete this.searchInfo[key1];
      }
      this.closeFilterModel.nativeElement.click();
    }

    this.searchInfo[key] = value;
    this.searchInfo['start'] = 0;

    if (key === 'project_manager_id') return this.getJobs('getAllJobs');

    this.getJobs();
  }

  //Set job id to delete on confirm delete modal
  setDeleteJobId(id: number) {
    this.deleteJobId = id;
    this.getJobDepartmentData(id);
  }

  //To delete job by id
  deleteJob(id: number) {
    this.getJobAuditTrail(id);
    this.jobsService.deleteJob(id).subscribe(
      res => this.onJobDeleteSuccess(res),
      error => this.onJobDeleteError(error),
    );
  }

  //On success of delete permission api
  onJobDeleteSuccess(result) {
    if (!result?.data) {
      return;
    }

    this.closebutton1.nativeElement.click();

    let status_from_data = 0,
      status_to_data = 0;
    if (this.auditTrailData) {
      this.sortByKey(this.auditTrailData, 'created_date', 'desc');
      status_from_data = this.auditTrailData[0].working_status_from;
      status_to_data = this.auditTrailData[0].working_status_to;
    }

    const newAuditTrail = {
      job_id: this.deleteJobId,
      user_id: this.userId,
      ip_address: this.ipAddress,
      action: 0,
      status_from: 2,
      status_to: 4,
      working_status_from: 0,
      working_status_to: 0,
      created_date: this.momentService.getDBDateTimeFormat(),
    };

    this.createJobAuditTrail(newAuditTrail);

    const newAuditTrail1 = {
      job_id: this.deleteJobId,
      user_id: this.userId,
      ip_address: this.ipAddress,
      action: 0,
      status_from: 0,
      status_to: 0,
      working_status_from: status_from_data,
      working_status_to: status_to_data,
      created_date: this.momentService.getDBDateTimeFormat(),
    };

    this.createJobAuditTrail(newAuditTrail1);
    this.getJobs();
    this.deleteJobId = '';
    this.jobDetail = '';
  }

  //On error of delete job api
  onJobDeleteError(error) {}

  //To get all owners
  getOwners() {
    //Service call to get all owners data
    this.ownerInfoService.getOwners().subscribe(
      res => this.onOwnersSuccess(res),
      error => this.onOwnersError(error),
    );
  }

  //On success of owners api call
  onOwnersSuccess(result) {
    if (!result?.data) return;

    this.ownersData = result.data;
  }

  //On error of owners api call
  onOwnersError(error) {}

  //To get all owners
  getYearFilters() {
    //Service call to get all owners data
    this.jobsService.getYearsFilter().subscribe(
      res => {
        this.onYearFilerSuccess(res);
      },
      error => this.onYearFilterError(error),
    );
  }

  //On success of owners api call
  onYearFilerSuccess(result) {
    if (!result?.data) return;

    this.yearFilterData = result.data;
  }

  //On error of owners api call
  onYearFilterError(error) {}

  //To get job department data for department model
  getJobDepartmentData(id) {
    this.jobsService.getJobById(id).subscribe(
      res => this.onDepartmentJobSuccess(res),
      error => this.onDepartmentJobError(error),
    );
  }

  //On success of job department api call
  onDepartmentJobSuccess(result) {
    if (result?.data) {
      this.jobDetail = result.data;
      return;
    }

    this.jobDetail = result.errors;
  }

  //On error of job department api call
  onDepartmentJobError(error) {}

  //To save filter data
  saveFilter() {
    this.localStorageService.setItem('jobFilter', JSON.stringify(this.searchInfo));
    this.getJobs();
  }

  //To clear filter data
  clearFilter() {
    this.localStorageService.removeItem('jobFilter');
    this.searchInfo = {
      project_manager_id: '',
      assistant_id: '',
      foreman_id: '',
      status: '',
      search: '',
      department_codes: '',
      owner_id: '',
      start: 0,
      field: '',
      order: '',
      year: '16',
      is_precon_involved: '',
      turnover_complete: '',
      submit_to_accounting: '',
    };
    this.getJobs();
    this.triggerChange('#project_manager_id');
    this.triggerChange('#assistant_id');
    this.triggerChange('#foreman_id');
    this.triggerChange('#status');
    this.triggerChange('#department_codes');
    this.triggerChange('#owner_id');
    this.triggerChange('#is_precon_involved');
    this.triggerChange('#turnover_complete');
    this.triggerChange('#submit_to_accounting');
    this.clearSelect2MultipleWithCheck();
    this.setSelect2MultipleWithCheck();
  }

  //To create job audit trail
  createJobAuditTrail(auditTrailArr) {
    this.jobAuditTrailService.createJobAuditTrail(auditTrailArr).subscribe(
      res => this.onCreateJobAuditTrailSuccess(res),
      error => this.onCreateJobAuditTrailError(error),
    );
  }

  //On success of create job audit trail
  onCreateJobAuditTrailSuccess(result) {}

  //On error of create job audit trail
  onCreateJobAuditTrailError(error) {}

  //To get ip address of client
  getIP() {
    this.ipAddressService.getIPAddress().subscribe((res: any) => {
      this.ipAddress = res.ip;
    });
  }

  setLoadFocus() {
    this.clearAddJobForm();
    this.clearForm();
    setTimeout(() => {
      this.name.nativeElement.focus();
      this.loadSelect2();
    }, 1000);
  }

  setDataToControl(field, value) {
    if (field === 'name' && value === '') {
      this.loadInputFormControlError('#name', 'add');
    } else {
      this.loadInputFormControlError('#name', 'remove');
    }

    const val = field === 'name' ? value.toUpperCase() : value;
    this.addJobForm.get(field).setValue(val);
    this.loadErrorStatus();
  }

  clearAddJobForm() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `clearForm('#myJobForm');`;
    this.renderer2.appendChild(this._document.body, s);
  }

  clearForm() {
    this.addJobForm.reset();
    this.loadInputFormControlError('#name', 'remove');
    this.loadErrorStatus();
    this.subDepartmentsData = [];
  }

  loadErrorStatus() {
    setTimeout(() => {
      this.loadSelect2Error();
    }, 200);
  }

  getOrder(data) {
    return `data-order = '` + data + `'`;
  }

  openCreateNewJobModel() {
    this.closebutton2.nativeElement.click();

    setTimeout(() => {
      this.createNewJob.nativeElement.click();
      this.setLoadFocus();
    }, 500);
  }

  //To get all job audit trail
  getJobAuditTrail(jobId) {
    this.jobAuditTrailService.getJobAuditTrail(jobId).subscribe(
      res => this.onGetJobAuditTrailSuccess(res),
      error => console.error(error),
    );
  }

  //On success of get job audit trail
  onGetJobAuditTrailSuccess(result) {
    if (!result?.data) {
      return;
    }

    this.clearDatatable();
    this.auditTrailData = result.data;
  }

  //On error of get job audit trail
  onGetJobAuditTrailError(error) {}

  getLocalTime(date) {
    return this.momentService.getDateWithFormat(date, 'M/DD/YYYY');
  }

  checkIsSelected(id, type) {
    return !!this.searchInfo[type].includes(id);
  }

  loadSelect2MultipleWithCheck(className = '.select2MultipleWithCheck') {
    const s1 = this.renderer2.createElement('script');
    s1.type = 'text/javascript';
    s1.text = `loadSelect2MultipleWithCheck('` + className + `','Status');`;
    this.renderer2.appendChild(this._document.body, s1);
  }

  clearSelect2MultipleWithCheck(className = '.select2MultipleWithCheck') {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `clearSelect2MultipleWithCheck('` + className + `');`;
    this.renderer2.appendChild(this._document.body, s);
  }

  setSelect2MultipleWithCheck(className = '.select2MultipleWithCheck', searchInfo = {}) {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `setSelect2MultipleWithCheck('` + className + `', [` + searchInfo['status'] + `]);`;
    this.renderer2.appendChild(this._document.body, s);

    const s1 = this.renderer2.createElement('script');
    s1.type = 'text/javascript';
    s1.text = `setSelect2MultipleWithCheck('` + className + `', [` + searchInfo['department_codes'] + `]);`;
    this.renderer2.appendChild(this._document.body, s1);
  }

  saveExportFile(res) {
    const a = document.createElement('a');
    const reportDate = this.momentService.getGeneralDateFormat('', 'MM-DD-YYYY_h:mm A');

    const url = window.URL.createObjectURL(res.body);
    a.href = url;
    a.download = `Jobs_${reportDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  generateCSV() {
    if (!this.jobsData.length) {
      this.toastService.showToast('No data to export.', 'error');
      return;
    }

    this.jobsService.exportJobs(this.searchInfo, true, true).subscribe(res => this.saveExportFile(res));
  }
}
